import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import {
    deleteNotification,
    getNotifications,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    type NotificationsResponse
} from '../api/notifications.api';
import { processNotifications } from '../utils/notificationConverter';

export const useNotifications = () => {
  const queryClient = useQueryClient();
  const enablePolling =
    process.env.REACT_APP_ENABLE_NOTIFICATION_POLLING === 'true';

  // Query key for notifications
  const notificationsQueryKey = useMemo(() => ['notifications'], []);

  // Main query for notifications
  const {
    data: notificationsData,
    isLoading,
    error,
    refetch
  } = useQuery<NotificationsResponse>({
    queryKey: notificationsQueryKey,
    queryFn: () => getNotifications({ page: 1, limit: 20 }),
    refetchInterval: enablePolling ? 10000 : false,
    refetchIntervalInBackground: enablePolling,
    refetchOnWindowFocus: true,
    staleTime: 5000, // Consider data stale after 5 seconds
    gcTime: 1000 * 60 * 5 // Keep in cache for 5 minutes
  });

  // Extract data from response and process notifications
  const rawNotifications = notificationsData?.data?.notifications || [];
  const notifications = useMemo(() => {
    return processNotifications(rawNotifications);
  }, [rawNotifications]);

  const pagination = useMemo(
    () =>
      notificationsData?.data?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      },
    [notificationsData?.data?.pagination]
  );

  // Calculate unread count
  const unreadCount = notifications.filter(
    (notification) => notification.status === 'unread'
  ).length;

  // Mutation for marking single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationsQueryKey });

      // Snapshot the previous value
      const previousNotifications =
        queryClient.getQueryData<NotificationsResponse>(notificationsQueryKey);

      // Optimistically update to the new value
      if (previousNotifications) {
        const updatedNotifications =
          previousNotifications.data.notifications.map((notification) =>
            notification.notifications_id === notificationId
              ? { ...notification, status: 'read' as const }
              : notification
          );

        queryClient.setQueryData<NotificationsResponse>(notificationsQueryKey, {
          ...previousNotifications,
          data: {
            ...previousNotifications.data,
            notifications: updatedNotifications
          }
        });
      }

      // Return a context object with the snapshotted value
      return { previousNotifications };
    },
    onError: (err, notificationId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationsQueryKey,
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    }
  });

  // Mutation for marking all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationsQueryKey });

      // Snapshot the previous value
      const previousNotifications =
        queryClient.getQueryData<NotificationsResponse>(notificationsQueryKey);

      // Optimistically update to the new value
      if (previousNotifications) {
        const updatedNotifications =
          previousNotifications.data.notifications.map((notification) => ({
            ...notification,
            status: 'read' as const
          }));

        queryClient.setQueryData<NotificationsResponse>(notificationsQueryKey, {
          ...previousNotifications,
          data: {
            ...previousNotifications.data,
            notifications: updatedNotifications
          }
        });
      }

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationsQueryKey,
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    }
  });

  // Mutation for deleting a notification
  const deleteNotificationMutation = useMutation({
    mutationFn: deleteNotification,
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationsQueryKey });

      // Snapshot the previous value
      const previousNotifications =
        queryClient.getQueryData<NotificationsResponse>(notificationsQueryKey);

      // Optimistically update to the new value
      if (previousNotifications) {
        const updatedNotifications =
          previousNotifications.data.notifications.filter(
            (notification) => notification.notifications_id !== notificationId
          );

        queryClient.setQueryData<NotificationsResponse>(notificationsQueryKey, {
          ...previousNotifications,
          data: {
            ...previousNotifications.data,
            notifications: updatedNotifications,
            pagination: {
              ...previousNotifications.data.pagination,
              total: previousNotifications.data.pagination.total - 1
            }
          }
        });
      }

      return { previousNotifications };
    },
    onError: (err, notificationId, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationsQueryKey,
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    }
  });

  // Wrapper functions for mutations
  const markAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutation.mutate(notificationId);
    },
    [markAsReadMutation]
  );

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const deleteNotificationById = useCallback(
    (notificationId: string) => {
      deleteNotificationMutation.mutate(notificationId);
    },
    [deleteNotificationMutation]
  );

  // Load more notifications (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.page < pagination.totalPages && !isLoading) {
      // For now, we'll just refetch - you might want to implement infinite query here
      refetch();
    }
  }, [refetch, pagination, isLoading]);

  // Manual refresh function
  const refreshNotifications = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
  }, [queryClient, notificationsQueryKey]);

  // Listen for FCM notification events
  useEffect(() => {
    const handleFCMNotification = () => {
      refreshNotifications();
    };

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
        console.log('Notification clicked, refreshing notifications list');
        refreshNotifications();
      }
    };

    // Listen for FCM notification events
    window.addEventListener(
      'fcm-notification-received',
      handleFCMNotification as EventListener
    );

    // Listen for service worker messages (notification clicks)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'message',
        handleServiceWorkerMessage
      );
    }

    return () => {
      window.removeEventListener(
        'fcm-notification-received',
        handleFCMNotification as EventListener
      );
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener(
          'message',
          handleServiceWorkerMessage
        );
      }
    };
  }, [refreshNotifications]);

  return {
    notifications,
    unreadCount,
    loading: isLoading,
    error: error?.message || null,
    pagination,
    fetchNotifications: refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification: deleteNotificationById,
    loadMore,
    hasMore: pagination.page < pagination.totalPages,
    refreshNotifications,
    // Polling control (now handled by React Query)
    isPolling: enablePolling && !error,
    // Mutation states
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending
  };
};
